/**
 * AR Vision Demo — self-contained invoice detail view (Scene 2).
 *
 * THROWAWAY DEMO CODE. Renders a fixture-backed invoice detail for the flagship
 * "Harbor Foods Co." order, including an **Emails** tab that shows the sent invoice
 * email plus a PDF/letterhead viewer rendering the static branded asset
 * (`BRAND.letterheadUrl`). This deliberately bypasses the generic
 * `EntityDetailTabs` system (which would make real RPC association/email calls
 * the demo can't serve) so the whole scene renders from the fixture store with
 * zero backend calls.
 *
 * Mounted only from `ar-invoice-detail-slidein.tsx` behind an `isArDemoMode()`
 * guard for the flagship invoice id. Delete with `src/demo/ar`.
 */

import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { format } from 'date-fns';
import { EnvelopeSimpleIcon, FilePdfIcon } from '@phosphor-icons/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Currency } from '@/components/common/Currency';
import { getInvoiceStatusBadge } from '@/components/accounts-receivable/invoice-status-badge';
import { useArDemoStore } from './fixture-store';
import { BRAND } from './fixture-seed';
import type { DemoArInvoiceDetail } from './fixture-store.types';

function formatIso(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return format(d, 'MMM d, yyyy, h:mm a');
}

function formatDateOnly(value: string | null | undefined): string {
  if (!value) return '—';
  // DATEONLY strings are 'yyyy-MM-dd'; render without timezone shifting.
  const [y, m, day] = value.split('-').map(Number);
  if (!y || !m || !day) return value;
  return format(new Date(y, m - 1, day), 'MM/dd/yyyy');
}

/** The fixture-backed invoice detail for the flagship (Scene 2). */
export function DemoInvoiceDetail({ invoiceId }: { invoiceId: string }) {
  const invoice = useArDemoStore((s) => s.invoicesById[invoiceId]);
  // The send event records the branded-letterhead asset path; fall back to the
  // single-source-of-truth BRAND path so the viewer always has an image.
  const sentEvent = invoice?.invoiceEvents?.find(
    (e) => e.eventType === 'invoice_sent',
  );
  const payloadLetterhead = sentEvent?.payload?.letterheadUrl;
  const letterheadUrl =
    typeof payloadLetterhead === 'string'
      ? payloadLetterhead
      : BRAND.letterheadUrl;

  if (!invoice) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        <T>Invoice not found</T>
      </div>
    );
  }

  return (
    <Tabs defaultValue="emails" className="flex flex-col flex-1 min-h-0 gap-0">
      <SheetHeader className="shrink-0 grow-0">
        <div className="flex items-center gap-2">
          <SheetTitle>{invoice.invoiceNumber}</SheetTitle>
          {getInvoiceStatusBadge(invoice.status)}
        </div>
        <TabsList className="mt-2">
          <TabsTrigger value="invoice">
            <T>Invoice</T>
          </TabsTrigger>
          <TabsTrigger value="emails">
            <EnvelopeSimpleIcon className="size-4" />
            <T>Emails</T>
          </TabsTrigger>
        </TabsList>
      </SheetHeader>

      <div className="flex-1 min-h-0 overflow-hidden">
        <TabsContent value="invoice" className="h-full overflow-y-auto">
          <DemoInvoiceSummary invoice={invoice} />
        </TabsContent>
        <TabsContent value="emails" className="h-full overflow-hidden">
          <DemoSentEmail invoice={invoice} letterheadUrl={letterheadUrl} />
        </TabsContent>
      </div>
    </Tabs>
  );
}

function DemoInvoiceSummary({ invoice }: { invoice: DemoArInvoiceDetail }) {
  return (
    <div className="flex flex-col gap-4 px-6 py-6">
      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
        <span className="text-muted-foreground">
          <T>Customer</T>
        </span>
        <span className="font-medium">{invoice.customerName}</span>
        <span className="text-muted-foreground">
          <T>PO Reference</T>
        </span>
        <span>{invoice.customerPurchaseOrderReference ?? '—'}</span>
        <span className="text-muted-foreground">
          <T>Invoice Date</T>
        </span>
        <span>{formatDateOnly(invoice.invoiceDate)}</span>
        <span className="text-muted-foreground">
          <T>Due Date</T>
        </span>
        <span>{formatDateOnly(invoice.dueDate)}</span>
        <span className="text-muted-foreground">
          <T>Terms</T>
        </span>
        <span>{invoice.paymentTermsDescription ?? '—'}</span>
        <span className="text-muted-foreground">
          <T>Total</T>
        </span>
        <Currency
          value={invoice.netAmount}
          currency={invoice.currencyCode ?? 'USD'}
          className="font-semibold"
          fallback="—"
        />
        <span className="text-muted-foreground">
          <T>Balance Due</T>
        </span>
        <Currency
          value={invoice.balanceDue}
          currency={invoice.currencyCode ?? 'USD'}
          className="font-semibold"
          fallback="—"
        />
      </div>

      <div className="rounded border">
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 border-b px-3 py-2 text-xs font-semibold text-muted-foreground">
          <span>
            <T>Description</T>
          </span>
          <span className="text-right">
            <T>Qty</T>
          </span>
          <span className="text-right">
            <T>Rate</T>
          </span>
          <span className="text-right">
            <T>Amount</T>
          </span>
        </div>
        {invoice.invoiceLineItems.map((line) => (
          <div
            key={line.id}
            className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 px-3 py-2 text-sm border-b last:border-b-0"
          >
            <span>{line.description}</span>
            <span className="text-right">{line.quantity}</span>
            <Currency
              value={line.rate}
              currency={invoice.currencyCode ?? 'USD'}
              className="text-right"
              fallback="—"
            />
            <Currency
              value={line.amount}
              currency={invoice.currencyCode ?? 'USD'}
              className="text-right font-medium"
              fallback="—"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function DemoSentEmail({
  invoice,
  letterheadUrl,
}: {
  invoice: DemoArInvoiceDetail;
  letterheadUrl: string;
}) {
  const gt = useGT();
  const [imageError, setImageError] = useState(false);
  const email = invoice.outboundEmails[0];
  const sentAtFormatted = formatIso(email?.sentAt ?? invoice.firstSentAt);
  // Non-translatable values (addresses, status enum, filename) bound here so
  // they aren't JSX string literals (i18nsure jsx-only mode flags those).
  const fromAddress = email?.from ?? 'ar@gently-demo.com';
  const toAddress = email?.to ?? 'ap@harborfoods.com';
  const emailStatus = email?.status ?? 'sent';
  const pdfFileName = `${invoice.invoiceNumber}.pdf`;

  return (
    <div className="flex flex-col h-full overflow-y-auto px-6 py-6 gap-4">
      <div className="flex items-center gap-2">
        <EnvelopeSimpleIcon className="h-5 w-5 text-muted-foreground" />
        <h3 className="text-sm font-semibold">
          {email?.subject ?? `Invoice ${invoice.invoiceNumber} from Gently`}
        </h3>
      </div>

      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <span className="text-muted-foreground">
          <T>From</T>
        </span>
        <span className="truncate">{fromAddress}</span>
        <span className="text-muted-foreground">
          <T>To</T>
        </span>
        <span className="truncate">{toAddress}</span>
        <span className="text-muted-foreground">
          <T>Status</T>
        </span>
        <span>{emailStatus}</span>
        {sentAtFormatted && (
          <>
            <span className="text-muted-foreground">
              <T>Sent</T>
            </span>
            <span>{sentAtFormatted}</span>
          </>
        )}
      </div>

      <p className="text-sm text-foreground">
        <T>
          Please find attached the invoice for your recent order. The PDF on our
          branded letterhead is shown below.
        </T>
      </p>

      <div className="rounded border bg-muted/30">
        <div className="flex items-center gap-2 border-b px-3 py-2 text-xs font-medium text-muted-foreground">
          <FilePdfIcon className="size-4" />
          <span>{pdfFileName}</span>
          <span className="ml-auto">
            <T>Branded letterhead</T>
          </span>
        </div>
        <div className="flex justify-center bg-white p-4">
          {imageError ? (
            <div className="py-12 text-sm text-muted-foreground">
              <T>Letterhead preview unavailable</T>
            </div>
          ) : (
            <img
              src={letterheadUrl}
              alt={gt('Invoice on branded letterhead', {
                context: 'AR demo letterhead image alt text',
              })}
              className="max-w-full rounded border shadow-sm"
              onError={() => setImageError(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
